// macadamia-app/src/macadamia-core/components/hooks/usePostIts.ts
import { useState, useCallback, useEffect } from 'react';
import { IPostIt, POSTIT_DEFAULTS, viewportToContainer, migratePostItToAnchor } from '../postits/types';
import { v4 as uuidv4 } from 'uuid';
import { apiClient } from '../../../utils/apiClient';

interface UsePostItsProps {
    dossierId: string;
    chapterId: string;
    userId: string;
    containerBounds: DOMRect | null;
    scrollContainer: HTMLElement | null;
}

export const usePostIts = ({ dossierId, chapterId, userId, containerBounds, scrollContainer }: UsePostItsProps) => {
    const [postits, setPostits] = useState<IPostIt[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // ========================================
    // FETCH POST-ITS
    // ========================================

    const fetchPostIts = useCallback(async () => {
        if (!dossierId || !chapterId) return;

        try {
            setIsLoading(true);
            const response = await apiClient(
                `/dossiers/${dossierId}/chapters/${chapterId}/postits`
            );

            if (!response.ok) {
                throw new Error('Erreur lors du chargement des post-its');
            }

            const data = await response.json();
            // Migrer automatiquement les post-its vers le nouveau format
            const migratedPostits = (data.postits || []).map(migratePostItToAnchor);
            setPostits(migratedPostits);
        } catch (error) {
            console.error('Erreur lors du chargement des post-its:', error);
            setPostits([]);
        } finally {
            setIsLoading(false);
        }
    }, [dossierId, chapterId]);

    useEffect(() => {
        fetchPostIts();
    }, [fetchPostIts]);

    // ========================================
    // CREATE POST-IT
    // ========================================

    const createPostIt = useCallback(
        async (initialPosition?: { x: number; y: number }) => {
            if (!containerBounds) return;

            let containerPos: { x: number; y: number };

            if (initialPosition) {
                containerPos = {
                    x: initialPosition.x - POSTIT_DEFAULTS.WIDTH / 2,
                    y: initialPosition.y - POSTIT_DEFAULTS.HEIGHT / 2
                };
            } else {
                const centerViewportX = containerBounds.left + (containerBounds.width / 2);
                const centerViewportY = containerBounds.top + (containerBounds.height / 2);

                containerPos = viewportToContainer(centerViewportX, centerViewportY, containerBounds, scrollContainer);

                containerPos.x -= POSTIT_DEFAULTS.WIDTH / 2;
                containerPos.y -= POSTIT_DEFAULTS.HEIGHT / 2;
            }

            const newPostIt: IPostIt = {
                _id: uuidv4(),
                dossierId,
                chapterId,
                content: '',
                anchor: {
                    type: 'document',
                    documentPosition: containerPos
                },
                size: {
                    width: POSTIT_DEFAULTS.WIDTH,
                    height: POSTIT_DEFAULTS.HEIGHT
                },
                color: POSTIT_DEFAULTS.COLOR,
                visibility: 'shared',
                authorId: userId,
                createdAt: new Date(),
                updatedAt: new Date()
            };

            // Mise à jour optimiste
            setPostits((prev) => [...prev, newPostIt]);

            // Sauvegarde backend
            try {
                const response = await apiClient(
                    `/dossiers/${dossierId}/chapters/${chapterId}/postits`,
                    {
                        method: 'POST',
                        body: JSON.stringify(newPostIt)
                    }
                );

                if (!response.ok) {
                    throw new Error('Erreur lors de la création du post-it');
                }

                const savedPostIt = await response.json();

                setPostits((prev) =>
                    prev.map((p) => (p._id === newPostIt._id ? savedPostIt : p))
                );
            } catch (error) {
                console.error('Erreur lors de la création du post-it:', error);
                setPostits((prev) => prev.filter((p) => p._id !== newPostIt._id));
            }
        },
        [dossierId, chapterId, userId, containerBounds, scrollContainer]
    );

    // ========================================
    // UPDATE POST-IT
    // ========================================

    const updatePostIt = useCallback(
        async (updatedPostIt: IPostIt) => {
            setPostits((prev) =>
                prev.map((p) => (p._id === updatedPostIt._id ? updatedPostIt : p))
            );

            try {
                const response = await apiClient(
                    `/dossiers/${dossierId}/chapters/${chapterId}/postits/${updatedPostIt._id}`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(updatedPostIt)
                    }
                );

                if (!response.ok) {
                    throw new Error('Erreur lors de la mise à jour du post-it');
                }
            } catch (error) {
                console.error('Erreur lors de la mise à jour du post-it:', error);
            }
        },
        [dossierId, chapterId]
    );

    // ========================================
    // DELETE POST-IT
    // ========================================

    const deletePostIt = useCallback(
        async (postItId: string) => {
            const deletedPostIt = postits.find((p) => p._id === postItId);

            setPostits((prev) => prev.filter((p) => p._id !== postItId));

            try {
                const response = await apiClient(
                    `/dossiers/${dossierId}/chapters/${chapterId}/postits/${postItId}`,
                    { method: 'DELETE' }
                );

                if (!response.ok) {
                    throw new Error('Erreur lors de la suppression du post-it');
                }
            } catch (error) {
                console.error('Erreur lors de la suppression du post-it:', error);
                if (deletedPostIt) {
                    setPostits((prev) => [...prev, deletedPostIt]);
                }
            }
        },
        [dossierId, chapterId, postits]
    );

    // ========================================
    // CHANGE COLOR
    // ========================================

    const changeColor = useCallback(
        async (postItId: string, newColor: IPostIt['color']) => {
            const postit = postits.find((p) => p._id === postItId);
            if (!postit) return;

            const updatedPostIt: IPostIt = {
                ...postit,
                color: newColor,
                updatedAt: new Date()
            };

            await updatePostIt(updatedPostIt);
        },
        [postits, updatePostIt]
    );

    // ========================================
    // CHANGE VISIBILITY
    // ========================================

    const changeVisibility = useCallback(
        async (postItId: string, newVisibility: IPostIt['visibility']) => {
            const postit = postits.find((p) => p._id === postItId);
            if (!postit) return;

            const updatedPostIt: IPostIt = {
                ...postit,
                visibility: newVisibility,
                updatedAt: new Date()
            };

            await updatePostIt(updatedPostIt);
        },
        [postits, updatePostIt]
    );

    return {
        postits,
        isLoading,
        createPostIt,
        updatePostIt,
        deletePostIt,
        changeColor,
        changeVisibility,
        refetch: fetchPostIts
    };
};
